import {DispatchActionType} from '../models/DispatchActionType';
import {GatewayEligibility, type IPaymentConfiguration, type GatewayConfiguration, type GatewayEligibilityDetails} from '../models/GatewayConfiguration';
import {type DispatchAction, store} from '../store';
import {getLocaleText} from '../util/translation';
import {createDispatchUpdate} from './initialState';

function paymentConfigurationReducer(state: IPaymentConfiguration, action: DispatchAction<unknown>): IPaymentConfiguration {
	switch (action.type) {
		case DispatchActionType.PAYMENT_REGISTER_GATEWAY_BATCH: {
			return {
				...state,
				gatewayConfigurations: {
					...state.gatewayConfigurations,
					...(action as DispatchAction<Record<string, GatewayConfiguration>>).payload,
				},
			};
		}

		case DispatchActionType.PAYMENT_UPDATE_AVAILABLE_GATEWAYS: {
			const {payload} = action as DispatchAction<IPaymentConfiguration['availableGateways']>;
			return {
				...state,
				availableGateways: payload,
			};
		}

		case DispatchActionType.PAYMENT_UPDATE_GATEWAY_AVAILABILITY_DETAILS: {
			const {payload} = action as DispatchAction<IPaymentConfiguration['gatewayAvailabilityDetails']>;
			return {
				...state,
				gatewayAvailabilityDetails: payload,
			};
		}

		case DispatchActionType.PAYMENT_SET_SELECTED_GATEWAY: {
			const {payload} = action as DispatchAction<string>;
			return {
				...state,
				selectedGateway: payload,
			};
		}

		default:
			return {
				...state,
			};
	}
}

// ======================= Dispatch Helpers =============================
const registerGatewayBatch = createDispatchUpdate<Record<string, GatewayConfiguration>>(DispatchActionType.PAYMENT_REGISTER_GATEWAY_BATCH);
const setSelectedPaymentGateway = createDispatchUpdate<string>(DispatchActionType.PAYMENT_SET_SELECTED_GATEWAY);
const updateAvailableGateways = createDispatchUpdate<string[]>(DispatchActionType.PAYMENT_UPDATE_AVAILABLE_GATEWAYS);
const updateGatewayDetails = createDispatchUpdate<Record<string, GatewayEligibilityDetails>>(DispatchActionType.PAYMENT_UPDATE_GATEWAY_AVAILABILITY_DETAILS);

// ======================= Store Selectors =============================

type GatewayEligibilityContext = {eligibility: GatewayEligibility; config: GatewayConfiguration; displayIndex?: number};

const PaymentConfiguration = {
	data: () => (store.getState().paymentConfiguration),
	selectedGateway: () => store.getState().paymentConfiguration.selectedGateway,
	gatewayConfig: (gatewayId: string) => store.getState().paymentConfiguration.gatewayConfigurations[gatewayId] ?? null,
	eligibleGatewayDetails(gatewayId: string): GatewayEligibilityDetails | null {
		const config = store.getState().paymentConfiguration.gatewayConfigurations[gatewayId];
		if (!config) {
			return null;
		}

		const details = store.getState().paymentConfiguration.gatewayAvailabilityDetails[gatewayId] ?? {};

		if (config.browser === false) {
			details.browser = {
				explanation: getLocaleText('This payment method is not supported by your browser. Please try a different option.'),
			};
		}

		if (config.initialized === false) {
			details.initialized = {
				explanation: getLocaleText('Something went wrong initializing this payment method. Please try a different option or try again later.'),
			};
		}

		return details;
	},
	sortGatewaysByEligibility(): GatewayEligibilityContext[] {
		let displayIndex = 0;
		const sortedEligibility = Object.values(store.getState().paymentConfiguration.gatewayConfigurations)
			.map<GatewayEligibilityContext>(config => ({config, eligibility: PaymentConfiguration.eligibleGateway(config.gatewayId)}))
			// Second we sort on the available gateways array so the methods are displayed in the same order as
			// configured by WC. If the method is ineligible the existing ordering should not be touched.
			.sort((a, b) => {
				const indexA = store.getState().paymentConfiguration.availableGateways.indexOf(a.config.gatewayId);
				const indexB = store.getState().paymentConfiguration.availableGateways.indexOf(b.config.gatewayId);

				return indexA - indexB;
			})
			// First sorting on just eligibility. This puts all the in eligible gateways at the end of the array.
			.sort((a, b) => a.eligibility - b.eligibility)
			.map(context => {
				if (context.eligibility) {
					context.displayIndex = displayIndex;
					displayIndex++;
				}

				return context;
			});

		const selectedContextIndex = sortedEligibility.findIndex(context => context.config.gatewayId === PaymentConfiguration.selectedGateway());
		const selectedContext = sortedEligibility[selectedContextIndex];

		if (selectedContext?.displayIndex && selectedContext.displayIndex > 2) {
			// Remove the selected context from the array.
			sortedEligibility.splice(selectedContextIndex, 1);

			// Get the index to place the selected context at.
			const spliceIndex = sortedEligibility.findIndex(context => context.displayIndex === 2);

			// Splice the selected context into the array at the correct index.
			sortedEligibility.splice(spliceIndex, 0, selectedContext);

			// We have to remap the display index again after changing the order so the UI is rendered correctly.
			let updatedDisplayIndex = 0;
			return sortedEligibility.map(context => {
				context.displayIndex = undefined;
				if (context.eligibility) {
					context.displayIndex = updatedDisplayIndex;
					updatedDisplayIndex++;
				}

				return context;
			});
		}

		return sortedEligibility;
	},
	eligibleGateway(gatewayId: string): GatewayEligibility {
		const config = store.getState().paymentConfiguration.gatewayConfigurations[gatewayId];
		if (!config) {
			return GatewayEligibility.NotEligible;
		}

		if (!store.getState().paymentConfiguration.availableGateways.includes(config.gatewayId)) {
			return GatewayEligibility.NotEligible;
		}

		const availabilityDetails = PaymentConfiguration.eligibleGatewayDetails(gatewayId);
		// Lack of availability details means the gateway is eligible if it is also part of the availableGateways array.
		if (!availabilityDetails || Object.keys(availabilityDetails).length === 0) {
			return GatewayEligibility.Eligible;
		}

		if (availabilityDetails.minimum) {
			return GatewayEligibility.EligibleWithChange;
		}

		if (availabilityDetails.maximum) {
			return GatewayEligibility.EligibleWithChange;
		}

		if (availabilityDetails.currency) {
			if (availabilityDetails.currency.available_options?.length) {
				return GatewayEligibility.EligibleWithChange;
			}

			return GatewayEligibility.NotEligible;
		}

		if (availabilityDetails.country?.available_options?.length) {
			if (availabilityDetails.country.available_options?.length) {
				return GatewayEligibility.EligibleWithChange;
			}

			return GatewayEligibility.NotEligible;
		}

		if (config.browser === false) {
			return GatewayEligibility.NotEligible;
		}

		if (config.initialized === false) {
			return GatewayEligibility.EligibleButErrored;
		}

		return GatewayEligibility.Eligible;
	},
	eligibleGatewayCount() {
		let count = 0;
		const data = PaymentConfiguration.data();

		for (const [gatewayId] of Object.entries(data.gatewayConfigurations)) {
			if (PaymentConfiguration.eligibleGateway(gatewayId)) {
				count++;
			}
		}

		return count;
	},
	checkEligibleOrFindAlternate(gatewayId: string): string | null {
		if (PaymentConfiguration.eligibleGateway(gatewayId)) {
			return gatewayId;
		}

		return PaymentConfiguration.firstEligibleMethod();
	},
	firstEligibleMethod(): string | null {
		const gatewayContexts = PaymentConfiguration.sortGatewaysByEligibility();

		for (const context of gatewayContexts) {
			if (context.eligibility !== GatewayEligibility.NotEligible) {
				return context.config.gatewayId;
			}
		}

		return null;
	},
};

export {
	type GatewayEligibilityContext,
	PaymentConfiguration,
	paymentConfigurationReducer,
	registerGatewayBatch,
	updateAvailableGateways,
	updateGatewayDetails,
	setSelectedPaymentGateway,
};
